<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import SlideOver from '../SlideOver.svelte';
  import { InteractionType } from '$shared/lib';
  import CommentNotification from './CommentNotification.svelte';
  import { toastError } from '$utils/toasts';
  import { client } from '$lib/api/Client';
  import type { Notification } from '$shared/lib';
  import LikeNotification from './LikeNotification.svelte';
  import AnnouncementNotification from './AnnouncementNotification.svelte';
  import HackathonTeamMemberNotification from './HackathonNewTeamMemberNotification.svelte';
  import { default as NotificationComponent } from '../Notification.svelte';
  import { notificationIds } from '$stores';
  import InfiniteScroll from '$components/InfiniteScroll.svelte';

  const dispatch = createEventDispatcher();
  export let showNotifications = false;
  let notifications: Notification[] = [];
  let hasMore = false;
  let loadingNotifications = true;
  let offset = 0;
  let DEFAULT_PAGE_SIZE = 8;
  const since = String(Date.now());
  onMount(async () => {
    await loadNotifications();
  });
  onDestroy(async () => {
    if ($notificationIds?.length > 0) await client.notifications.markAsRead($notificationIds);
    dispatch('notificationsRead');
  });

  const loadNotifications = async () => {
    loadingNotifications = true;
    try {
      const notificationsResponse = await client.notifications.listNotifications({
        offset: String(offset),
        since,
      });
      hasMore = notificationsResponse?.hasMore;
      notifications = [...notifications, ...(notificationsResponse?.notifications ?? [])];
      offset += DEFAULT_PAGE_SIZE;
      loadingNotifications = false;
    } catch (error) {
      toastError(error);
      loadingNotifications = false;
    }
  };
  $: unreadNotifications = notifications?.filter((notification) => !notification.isRead);
  $: readNotifications = notifications?.filter((notification) => notification.isRead);
</script>

<SlideOver title="// Notifications" bind:slideOverOpen={showNotifications}>
  {#if notifications?.length < 1 && !loadingNotifications}
    <div class="flex h-full w-full items-center justify-center">
      <NotificationComponent
        informational
        title="No notifications yet"
        description="When you get new notifications about content on the platform, you'll find them here."
      />
    </div>
  {/if}
  <div class="space-y-4">
    {#if unreadNotifications && unreadNotifications?.length > 0}
      {#each unreadNotifications as notification}
        {#if notification.interactionType === InteractionType.COMMENT || notification.interactionType === InteractionType.PROJECT_COMMENT}
          <CommentNotification bind:notification />
        {:else if notification.interactionType === InteractionType.LIKE || notification.interactionType === InteractionType.PROJECT_LIKE}
          <LikeNotification bind:notification />
        {:else if notification.interactionType === InteractionType.ANNOUNCEMENT}
          <AnnouncementNotification bind:notification />
        {:else if notification.interactionType === InteractionType.TEAM_MEMBER_ADDED}
          <HackathonTeamMemberNotification bind:notification />
        {/if}
      {/each}
    {/if}
    {#if readNotifications && readNotifications?.length > 0}
      {#each readNotifications as notification}
        {#if notification.interactionType === InteractionType.COMMENT || notification.interactionType === InteractionType.PROJECT_COMMENT}
          <CommentNotification bind:notification />
        {:else if notification.interactionType === InteractionType.LIKE || notification.interactionType === InteractionType.PROJECT_LIKE}
          <LikeNotification bind:notification />
        {:else if notification.interactionType === InteractionType.ANNOUNCEMENT}
          <AnnouncementNotification bind:notification />
        {:else if notification.interactionType === InteractionType.TEAM_MEMBER_ADDED}
          <HackathonTeamMemberNotification bind:notification />
        {/if}
      {/each}
    {/if}
    <InfiniteScroll
      {hasMore}
      bind:loadingMore={loadingNotifications}
      on:load-more={async () => {
        await loadNotifications();
      }}
    />
  </div>
</SlideOver>
